import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import FeatureCard from 'tio-ui/components/feature-card';
import SectionHeader from 'tio-ui/components/section-header';
import CtaBanner from 'tio-ui/components/cta-banner';
import PageHero from 'tio-ui/components/page-hero';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhGraduationCap,
  PhHandshake,
  PhArrowRight,
  PhStar,
  PhMagnifyingGlass,
  PhCertificate,
  PhChalkboardTeacher,
  PhBookOpen,
  PhBuildingOffice,
  PhSparkle,
} from 'ember-phosphor-icons';

interface Partner {
  id: string;
  name: string;
  category: string;
  description: string;
  discount: string;
  rating: number;
  programs: number;
  featured: boolean;
}

const DEMO_PARTNERS: Partner[] = [
  {
    id: 'arizona-state',
    name: 'Arizona State University',
    category: 'University',
    description:
      "Top-ranked online programs tailored for working professionals. Earn your degree on your schedule with ASU's flexible format.",
    discount: 'Up to 46% tuition reduction',
    rating: 4.8,
    programs: 120,
    featured: true,
  },
  {
    id: 'purdue-global',
    name: 'Purdue University Global',
    category: 'University',
    description:
      'Career-focused online degrees with credit for work experience. Personalized learning paths for adult learners.',
    discount: 'Up to 38% tuition reduction',
    rating: 4.6,
    programs: 85,
    featured: true,
  },
  {
    id: 'coursera-business',
    name: 'Coursera for Business',
    category: 'Online Platform',
    description:
      'Access certificates from Google, IBM, Meta, and top universities. Build skills in data, tech, and leadership.',
    discount: 'Employer-sponsored access',
    rating: 4.7,
    programs: 500,
    featured: false,
  },
  {
    id: 'guild-education',
    name: 'Guild Education',
    category: 'Education Network',
    description:
      'Curated learning marketplace connecting you to high-quality programs aligned with career advancement.',
    discount: 'Tuition-free programs available',
    rating: 4.5,
    programs: 200,
    featured: false,
  },
  {
    id: 'strayer-university',
    name: 'Strayer University',
    category: 'University',
    description:
      'Flexible online and hybrid programs designed for working adults. Accelerated formats to finish faster.',
    discount: 'Up to 25% tuition reduction',
    rating: 4.3,
    programs: 45,
    featured: false,
  },
  {
    id: 'udemy-business',
    name: 'Udemy Business',
    category: 'Online Platform',
    description:
      'On-demand courses in tech, business, and personal development. Learn at your own pace with expert instructors.',
    discount: 'Full catalog access included',
    rating: 4.4,
    programs: 3000,
    featured: false,
  },
];

export default class LpnIndexPage extends Component {
  @service declare reimaginedData: ReimaginedDataService;

  get partners() {
    return DEMO_PARTNERS;
  }

  get featuredPartners() {
    return this.partners.filter((p) => p.featured);
  }

  get allPartners() {
    return this.partners;
  }

  get categories() {
    return [...new Set(this.partners.map((p) => p.category))];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      <PageHero @gradient="cool" @decoration="circles" class="tio-lpn-hero">
        <div class="tio-row mb-4">
          <div class="tio-lpn-hero__icon">
            <PhHandshake class="h-7 w-7 tio-fg-inverse" />
          </div>
          <div>
            <Heading @level={{2}} class="tio-fg-inverse font-bold">
              Learning Partner Network
            </Heading>
            <Text @variant="inherit" @size="sm" class="tio-lpn-hero__subtitle">
              Exclusive education partners with discounted programs
            </Text>
          </div>
        </div>

        <div class="tio-row-flush gap-8 mt-6">
          <div class="tio-lpn-hero__stats">
            <div class="text-center">
              <div class="tio-text-3xl-bold tio-fg-inverse">{{this.allPartners.length}}</div>
              <Text @variant="inherit" @size="sm" class="tio-lpn-hero__stat-label">
                Partners
              </Text>
            </div>
            <div class="text-center">
              <div class="tio-text-3xl-bold tio-fg-inverse">3,950+</div>
              <Text @variant="inherit" @size="sm" class="tio-lpn-hero__stat-label">
                Programs
              </Text>
            </div>
            <div class="text-center">
              <div class="tio-text-3xl-bold tio-fg-inverse">46%</div>
              <Text @variant="inherit" @size="sm" class="tio-lpn-hero__stat-label">
                Max Savings
              </Text>
            </div>
          </div>
        </div>
      </PageHero>

      <Card @padding="sm">
        <div class="tio-row flex-wrap">
          <div class="tio-row-tight flex-1 min-w-[200px] tio-surface-sunken px-3 py-2">
            <PhMagnifyingGlass class="tio-icon-sm tio-fg-disabled" />
            <Text @size="sm" class="tio-fg-disabled">Search partners, programs, or topics...</Text>
          </div>
          {{#each this.categories as |cat|}}
            <Chip @intent="primary">{{cat}}</Chip>
          {{/each}}
        </div>
      </Card>

      <SectionHeader
        @icon={{PhSparkle}}
        @title="Featured Partners"
        @subtitle="Curated programs with the best value for your goals"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        {{#each this.featuredPartners as |partner|}}
          <div class="tio-bg-surface rounded-xl border-2 tio-border-info p-6 relative">
            <div
              class="absolute -top-3 right-4 tio-row-extra-tight tio-bg-info tio-fg-inverse px-3 py-1 rounded-full tio-text-xs-medium"
            >
              <PhSparkle class="tio-icon-2xs" />
              Featured
            </div>
            <div class="tio-row-top-loose">
              <div class="tio-row-center h-14 w-14 rounded-xl tio-bg-info-soft shrink-0">
                <PhBuildingOffice class="h-7 w-7 tio-fg-info" />
              </div>
              <div class="flex-1">
                <Heading @level={{3}} class="font-bold">{{partner.name}}</Heading>
                <div class="tio-row-tight mt-1 mb-2">
                  <Chip @intent="primary">{{partner.category}}</Chip>
                  <div class="tio-row-extra-tight">
                    <PhStar class="tio-icon-xs tio-fg-warning" />
                    <Text @size="sm" @weight="medium">{{partner.rating}}</Text>
                  </div>
                  <Text @size="sm" class="tio-fg-disabled">•</Text>
                  <Text @size="sm" class="tio-fg-muted">{{partner.programs}} programs</Text>
                </div>
                <Text @size="sm" class="tio-fg-secondary mb-3">
                  {{partner.description}}
                </Text>
                <div class="tio-row-between tio-bg-info-soft rounded-lg px-3 py-2">
                  <Text @size="sm" @weight="medium" class="tio-fg-info-body">
                    {{partner.discount}}
                  </Text>
                  <Button @intent="primary" @appearance="outlined">
                    Explore
                    <PhArrowRight class="tio-icon-xs ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        {{/each}}
      </div>

      <SectionHeader
        @icon={{PhGraduationCap}}
        @title="All Partners"
        @subtitle="Browse the complete network"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {{#each this.allPartners as |partner|}}
          <Card class="hover:tio-border-info transition-colors">
            <div class="tio-row mb-3">
              <div class="tio-row-center h-10 w-10 tio-surface-sunken">
                <PhGraduationCap class="tio-icon-md tio-fg-secondary" />
              </div>
              <div>
                <Heading @level={{4}} @size="sm">{{partner.name}}</Heading>
                <Chip @intent="primary">{{partner.category}}</Chip>
              </div>
            </div>
            <Text @size="xs" class="tio-fg-secondary line-clamp-2 mb-3">
              {{partner.description}}
            </Text>
            <div class="tio-row-between">
              <div class="tio-row-extra-tight">
                <PhStar class="tio-icon-2xs tio-fg-warning" />
                <Text @size="xs" @weight="medium">{{partner.rating}}</Text>
                <Text @size="xs" class="tio-fg-disabled ml-1">{{partner.programs}} programs</Text>
              </div>
              <Text @size="xs" @weight="medium" class="tio-fg-info">
                {{partner.discount}}
              </Text>
            </div>
          </Card>
        {{/each}}
      </div>

      <SectionHeader
        @icon={{PhHandshake}}
        @title="Why Use the Network?"
        @subtitle="Exclusive benefits for employees"
        @iconClass="tio-fg-success"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FeatureCard
          @icon={{PhCertificate}}
          @title="Verified Quality"
          @description="Every partner is vetted for program quality, outcomes, and student satisfaction."
          @color="emerald"
        />
        <FeatureCard
          @icon={{PhChalkboardTeacher}}
          @title="Flexible Learning"
          @description="Online, hybrid, and in-person options designed for working professionals."
          @color="blue"
        />
        <FeatureCard
          @icon={{PhBookOpen}}
          @title="Stackable Credentials"
          @description="Earn certificates that stack toward degrees. Start small, build big."
          @color="violet"
        />
      </div>

      <CtaBanner
        @heading="Not Sure Where to Start?"
        @description="Our career advisor can match you with the right partner and program based on your career goals and existing experience."
        @intent="brand"
      >
        <:action>
          <Button @route="authenticated.reimagined.advisor" @intent="primary">
            Get Matched
            <PhArrowRight class="tio-icon-sm ml-2" />
          </Button>
        </:action>
      </CtaBanner>
    </VStack>
  </template>
}
